import React, { useState, useRef, useEffect } from "react";
import { Button } from "primereact/button";
import { FileUpload } from "primereact/fileupload";
import { RadioButton } from "primereact/radiobutton";
import { Dialog } from "primereact/dialog";
import { Toast } from "primereact/toast";
import { InputTextarea } from "primereact/inputtextarea";
import { ProgressBar } from "primereact/progressbar";
import { BaseApiServiceImpl } from "../app_utils/api/BaseApiServiceImpl";
import { isEmpty } from "../app_utils/utils/Utils";
import { UserSessionUtils } from "../app_utils/utils/UserSessionUtils";

const SendSMS: React.FC = () => {
  const [message, setMessage] = useState("");
  const [recipient, setRecipient] = useState("");
  const [smsType, setSmsType] = useState<"Form" | "bulk">("Form");
  const [isPreviewDialogVisible, setPreviewDialogVisible] = useState(false);
  const [csvFile, setCsvFile] = useState<File | null>(null);
  const [isSaving, setIsSaving] = useState<boolean>(false);
  const [walletBalance, setWalletBalance] = useState<number>(0);
  const [pricePerCredit, setPricePerCredit] = useState<number>(1);
  const [smsCredits, setSmsCredits] = useState<number>(0);
  const [requiredCredits, setRequiredCredits] = useState<number>(0);
  const [isLoadingBalance, setIsLoadingBalance] = useState(false);

  const toast = useRef<Toast>(null);
  const userId = UserSessionUtils.getUserDetails()?.id;
  const userCountryId = UserSessionUtils.getUserDetails()?.countryId;

  const refreshBalance = () => {
    if (!userId) return;
    setIsLoadingBalance(true);
    new BaseApiServiceImpl("/api/v1/wallet-deposits/balance-info/" + userId)
      .getRequestWithJsonResponse({})
      .then((response) => {
        setWalletBalance(response?.data?.balance ?? 0);
        setSmsCredits(response?.data?.smsCredits ?? response?.data?.credits ?? 0);
        setPricePerCredit(response?.data?.pricePerCredit ?? 1);
        setIsLoadingBalance(false);
      })
      .catch(() => {
        setIsLoadingBalance(false);
      });
  };

  useEffect(() => {
    if (userId) {
      setIsLoadingBalance(true);
      new BaseApiServiceImpl("/api/v1/wallet-deposits/balance-info/" + userId)
        .getRequestWithJsonResponse({})
        .then((response) => {
          setWalletBalance(response?.data?.balance ?? 0);
          setSmsCredits(response?.data?.smsCredits ?? response?.data?.credits ?? 0);
          setPricePerCredit(response?.data?.pricePerCredit ?? 1);
          setIsLoadingBalance(false);
        })
        .catch(() => {
          setIsLoadingBalance(false);
        });
    }
  }, [userId, userCountryId]);

  useEffect(() => {
    const count = countRecipients();
    setRequiredCredits(count);
  }, [recipient]);

  const countRecipients = (): number => {
    if (smsType === "bulk" && csvFile) {
      return 0;
    }
    if (!recipient.trim()) return 0;
    const numbers = recipient.split(/[\n,]+/).map((item: any) => item.trim()).filter((item: any) => item.length > 0);
    return numbers.length;
  };

  const validatePhoneNumber = (phone: string): boolean => {
    const cleaned = phone.trim().replace(/[^0-9+]/g, "");
    if (cleaned.startsWith("+")) {
      return /^\+[1-9]\d{6,14}$/.test(cleaned);
    }
    return /^\d{7,15}$/.test(cleaned);
  };

  const validateForm = (): boolean => {
    let isFormValid: boolean = true;

    if (smsType === "Form") {
      const numbers = recipient.split(/[\n,]+/).map((item: any) => item.trim()).filter((item: any) => item.length > 0);
      if (numbers.length === 0) {
        toast.current?.show({
          severity: "error",
          summary: "Validation Error",
          detail: "Please enter at least one recipient phone number.",
        });
        return false;
      }
      const invalidNumbers = numbers.filter((num: string) => !validatePhoneNumber(num));
      if (invalidNumbers.length > 0) {
        toast.current?.show({
          severity: "error",
          summary: "Validation Error",
          detail: `Invalid phone number(s): ${invalidNumbers.join(", ")}. Use E.164 format (e.g., 256755170341 or +256755170341).`,
        });
        return false;
      }
    }

    if (!message.trim()) {
      toast.current?.show({
        severity: "error",
        summary: "Validation Error",
        detail: "Please enter a message.",
      });
      return false;
    }

    if (message.length > 160) {
      toast.current?.show({
        severity: "error",
        summary: "Validation Error",
        detail: "Message exceeds 160 characters. Please shorten your message.",
      });
      return false;
    }

    if (requiredCredits > smsCredits) {
      toast.current?.show({
        severity: "error",
        summary: "Insufficient Credits",
        detail: `You need ${requiredCredits} credits but only have ${smsCredits.toFixed(2)}. Please top up your wallet.`,
      });
      return false;
    }

    return isFormValid;
  };

  const clearForm = () => {
    setCsvFile(null);
    setMessage("");
    setRecipient("");
    toast.current?.show({
      severity: "success",
      summary: "Success",
      detail: `SMS sent successfully as ${smsType}.`,
    });
  };
  /**
   * This submits a save user request to the backoffice
   */
  const doSendSMS = async () => {
    if (!validateForm()) return;

    setIsSaving(true);
    try {
      if (smsType === "bulk" && csvFile) {
        const text = await csvFile.text();
        const lines = text.split(/\r?\n/).filter((line) => line.trim() !== "");
        const phoneNumbers = new Set<string>();
        for (const line of lines) {
          const cols = line.split(",");
          for (const col of cols) {
            const cleaned = col.trim().replace(/"/g, "");
            if (/^\d{7,15}$/.test(cleaned)) {
              phoneNumbers.add(cleaned);
            }
          }
        }
        if (phoneNumbers.size === 0) {
          toast.current?.show({
            severity: "error",
            summary: "Error",
            detail: "No valid phone numbers found in the CSV file.",
          });
          setIsSaving(false);
          return;
        }
        const data = {
          message: message,
          phoneNumbers: Array.from(phoneNumbers),
        };
        await new BaseApiServiceImpl("/api/v1/sms/ui/bulk").postRequestWithJsonResponse(data);
      } else {
        const data: any = {
          apiKey: null,
          messages: [
            {
              message: message,
              phoneNumbers: recipient.split(/[\n,]+/).map((item: any) => item.trim()),
            },
          ],
        };
        await new BaseApiServiceImpl("/api/v1/sms/ui").postRequestWithJsonResponse(data);
      }
      setIsSaving(false);
      clearForm();
      refreshBalance();
    } catch (error: any) {
      setIsSaving(false);
      toast.current?.show({
        severity: "error",
        summary: "Error",
        detail: error.message,
      });
    }
  };
  const handleSendSms = () => {
    if (smsType === "bulk" && !csvFile) {
      toast.current?.show({
        severity: "error",
        summary: "Error",
        detail: "Please upload a CSV file for bulk SMS.",
      });
      return;
    }
    if (smsType === "Form" && isEmpty(recipient)) {
      toast.current?.show({
        severity: "error",
        summary: "Error",
        detail: "Please enter recipient numbers.",
      });
      return;
    }
    doSendSMS();
  };

  return (
    <div className="p-m-4 card ">
      <h4 className="p-text-center">Send SMS</h4>
      <div className="grid ">
        <div className="col-12 md:col-8 grid ">
          <div className="col-12 m-1">
            <RadioButton
              disabled={isSaving}
              inputId="Form"
              value="Form"
              onChange={(e) => setSmsType(e.value)}
              checked={smsType === "Form"}
            />
            <label htmlFor="Form" className="ml-2">
              Direct Form
            </label>
            <RadioButton
              disabled={isSaving}
              inputId="bulk"
              value="bulk"
              onChange={(e) => setSmsType(e.value)}
              checked={smsType === "bulk"}
              className="ml-4"
            />
            <label htmlFor="bulk" className="ml-2">
              File Upload (csv)
            </label>
          </div>

          {smsType === "Form" && (
            <div className="col-12 ">
              <label htmlFor="recipient" className="p-d-block w-full">
                Recipient Phone Number (s)
              </label>
              <InputTextarea
                id="recipient"
                value={recipient}
                onChange={(e) => setRecipient(e.target.value)}
                placeholder="Enter phone number e.g 256755170341 or multiple Phone numbers e.g 256755170341,254787375269"
                className="w-full mt-1"
                rows={3}
                disabled={isSaving}
              />
            </div>
          )}

          <div className="col-12  p-mb-3">
            <label htmlFor="message" className="w-full p-d-block">
              Message <span className="text-500">({message.length}/160)</span>
            </label>
            <InputTextarea
              id="message"
              value={message}
              onChange={(e: any) => setMessage(e.target.value)}
              placeholder="Enter your message here"
              className="w-full mt-1"
              rows={5}
              disabled={isSaving}
            />
            {message.length > 160 && (
              <small className="p-error">Message exceeds 160 characters</small>
            )}
          </div>

          {smsType === "bulk" && (
            <div className="col-12 py-3">
              <FileUpload
                disabled={isSaving}
                name="csv"
                mode="basic"
                accept=".csv"
                customUpload
                auto
                uploadHandler={(e) => setCsvFile(e.files[0])}
                chooseLabel="Select CSV to upload"
                className="p-mt-2"
              />
            </div>
          )}
          <div className="col-12 md:col-4 ml-1  grid">
            <Button
              loading={isSaving}
              label="Preview"
              icon="pi pi-eye"
              className="col-5 mr-1 p-button-outlined p-button-primary"
              onClick={() => setPreviewDialogVisible(true)}
            />

            <Button
              loading={isSaving}
              label="Send Now"
              icon="pi pi-send"
              className="col-6 ml-1 p-button-success"
              onClick={handleSendSms}
              disabled={requiredCredits > smsCredits}
            />
          </div>
        </div>
        <div className="col-12 md:col-4">
          <div className="card" style={{ background: "#f4f6f9" }}>
            <h5 className="mb-3">SMS Cost Estimation</h5>
            <div className="mb-2">
              <span className="text-500">Wallet Balance:</span>
              <span className="font-bold ml-2">{isLoadingBalance ? "Loading..." : `${walletBalance.toFixed(2)} UGX`}</span>
            </div>
            <div className="mb-2">
              <span className="text-500">Price per Credit:</span>
              <span className="font-bold ml-2">{pricePerCredit.toFixed(2)} UGX</span>
            </div>
            <div className="mb-2">
              <span className="text-500">Available Credits:</span>
              <span className="font-bold ml-2">{smsCredits.toFixed(2)}</span>
            </div>
            <div className="mb-2">
              <span className="text-500">Required Credits:</span>
              <span className="font-bold ml-2">{requiredCredits}</span>
            </div>
            <div className="mb-3">
              <ProgressBar
                value={Math.min((smsCredits / Math.max(requiredCredits, 1)) * 100, 100)}
                style={{ height: "8px" }}
                className={requiredCredits > smsCredits ? "p-progressbar-danger" : "p-progressbar-success"}
              />
            </div>
            {requiredCredits > smsCredits && (
              <Button
                label="Top Up Wallet"
                icon="pi pi-wallet"
                className="p-button-warning w-full"
                onClick={() => {
                  window.location.href = "/wallet";
                }}
              />
            )}
          </div>

          <div title="Guidance and Manual" className="mt-3">
            <h3>Guidance and Manual</h3>
            <p>
              <strong>Step 1:</strong> Choose the SMS type - Form or File.
            </p>
            <p>
              <strong>Step 2:</strong> Enter the recipient's phone number if sending a Form SMS or
              multiple numbers separated with either a comma(,) or a new line.
            </p>
            <p>
              <strong>Step 3:</strong> Enter your message content.
            </p>
            <p>
              <strong>Step 4:</strong> Click "Preview" to review the message before sending.
            </p>
            <p>
              <strong>Step 5:</strong> Send the SMS and check for confirmation.
            </p>
          </div>
        </div>
      </div>
      <Toast ref={toast} />
      <Dialog
        header="SMS Preview"
        visible={isPreviewDialogVisible}
        style={{ width: "50vw" }}
        onHide={() => setPreviewDialogVisible(false)}
      >
        <p>
          <strong>Type:</strong> {smsType === "Form" ? "Form SMS" : "Bulk SMS"}
        </p>
        {smsType === "Form" && (
          <p>
            <strong>Recipient:</strong> {recipient}
          </p>
        )}
        <p>
          <strong>Message:</strong> {message}
        </p>
        {smsType === "bulk" && (
          <p>
            <strong>CSV File:</strong> {csvFile ? csvFile.name : "No file selected"}
          </p>
        )}
      </Dialog>
    </div>
  );
};

export default SendSMS;
